import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchmatters, deleteMatter, updateTeam, fetchTeam } from '../services/api';
import TabNavigation, { TABS } from '../components/TabNavigation';
import Header from '../components/Header';
import '../styles/Dashboard.css';

const FILTERS = {
  ALL: 'all',
  NEW: 'new',
  ACTIVE: 'active',
  PENDING: 'pending'
};

const STATUS = {
  NEW: 'New',
  IN_PROGRESS: 'In Progress',
  PENDING: 'Pending',
  COMPLETED: 'Completed',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled'
};

function Dashboard({ headlessMode = false, selectedDomain: propSelectedDomain, selectedRegion: propSelectedRegion }) {
  const [activeTab, setActiveTab] = useState(TABS.MATTERS);
  const [caseFilter, setCaseFilter] = useState(FILTERS.ALL);
  const [matters, setmatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(propSelectedDomain || 'Legal');
  const [selectedRegion, setSelectedRegion] = useState(propSelectedRegion || 'AUS');
  
  // Update local state when props change (when in headless mode)
  useEffect(() => {
    if (headlessMode) {
      if (propSelectedDomain) setSelectedDomain(propSelectedDomain);
      if (propSelectedRegion) setSelectedRegion(propSelectedRegion);
    }
  }, [headlessMode, propSelectedDomain, propSelectedRegion]);
  
  const domains = ['Legal', 'Medical'];
  const regions = ['AUS', 'NZL', 'MEX', 'GBR'];
  
  const { userProfile, availableTeams, switchTeam, logout } = useAuth();
  const navigate = useNavigate();

  // Effect to fetch team details and initialize domain and region on load
  useEffect(() => {
    const loadTeamDetails = async () => {
      if (userProfile && userProfile.teamId) {
        try {
          console.log("Fetching team details for initial load:", userProfile.teamId);
          const teamDetails = await fetchTeam(userProfile.teamId);
          console.log("Team details fetched on initial load:", teamDetails);
          
          // Update locale dropdown if available
          if (teamDetails.teamLocale) {
            setSelectedRegion(teamDetails.teamLocale);
          }
          
          // Update domain dropdown if available
          if (teamDetails.llmDomain) {
            // Convert API's uppercase format to title case for display
            const domain = teamDetails.llmDomain.charAt(0).toUpperCase() + 
                          teamDetails.llmDomain.slice(1).toLowerCase();
            setSelectedDomain(domain);
          }
        } catch (err) {
          console.error("Error fetching team details on initial load:", err);
          
          // Fallback to teamSettings if available in user profile
          if (userProfile.teamSettings) {
            if (userProfile.teamSettings.llmDomain) {
              // Convert API's uppercase format to title case for display
              const domain = userProfile.teamSettings.llmDomain.charAt(0).toUpperCase() + 
                            userProfile.teamSettings.llmDomain.slice(1).toLowerCase();
              setSelectedDomain(domain);
            }
            
            if (userProfile.teamSettings.teamLocale) {
              setSelectedRegion(userProfile.teamSettings.teamLocale);
            }
          }
        }
      }
    };
    
    loadTeamDetails();
  }, [userProfile]);

  useEffect(() => {
    if (!userProfile) {
      return;
    }
    
    if (!userProfile.teamId) {
      console.error('No teamId found in user profile. Cannot load matters.');
      setError('No team found for your account. Please contact support.');
      setLoading(false);
      return;
    }

    const loadmatters = async () => {
      try {
        setLoading(true);
        console.log('Loading matters for team:', userProfile.teamId);
        
        // Map filter names to actual status values
        const statusMap = {
          [FILTERS.ALL]: null,
          [FILTERS.NEW]: STATUS.NEW,
          [FILTERS.ACTIVE]: STATUS.IN_PROGRESS,
          [FILTERS.PENDING]: STATUS.PENDING,
        };
        
        const status = statusMap[caseFilter];
        const caseData = await fetchmatters(userProfile.teamId, status);
        console.log('Matter data received:', caseData);
        setmatters(caseData.items || []);
      } catch (err) {
        console.error('Error loading matters:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response ? {
            status: err.response.status,
            data: err.response.data
          } : null
        });
        setError('Failed to load matters: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };

    loadmatters();
  }, [userProfile, caseFilter]);

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('authToken');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };
  
  const handleTeamChange = async (e) => {
    const newTeamId = e.target.value;
    if (newTeamId && newTeamId !== userProfile.teamId) {
      const success = switchTeam(newTeamId);
      if (success) {
        // Reload matters for the new team
        setmatters([]);
        setLoading(true);
        setError(null);
        
        // Fetch team details to update locale and domain
        try {
          const teamDetails = await fetchTeam(newTeamId);
          console.log("Team details fetched:", teamDetails);
          
          // Update locale dropdown if available
          if (teamDetails.teamLocale) {
            setSelectedRegion(teamDetails.teamLocale);
          }
          
          // Update domain dropdown if available
          if (teamDetails.llmDomain) {
            // Convert API's uppercase format to title case for display
            const domain = teamDetails.llmDomain.charAt(0).toUpperCase() + 
                          teamDetails.llmDomain.slice(1).toLowerCase();
            setSelectedDomain(domain);
          }
        } catch (err) {
          console.error("Error fetching team details:", err);
          // Don't set error here as we still want to load matters
        }
      }
    }
  };
  
  // Handle tab changes
  useEffect(() => {
    if (activeTab === TABS.QUICK_ACTIONS) {
      navigate('/prompts');
    }
  }, [activeTab, navigate]);
  
  const handleDomainChange = async (e) => {
    const newDomain = e.target.value;
    setSelectedDomain(newDomain);
    
    // Clear any previous messages
    setError(null);
    setSuccessMessage(null);
    
    if (!userProfile || !userProfile.teamId) {
      console.error('No team ID available to update domain');
      setError('Team information is missing. Cannot update domain.');
      return;
    }
    
    try {
      console.log(`Updating team domain to ${newDomain}`);
      // Update team with new domain (llmDomain in the API)
      await updateTeam(userProfile.teamId, {
        llmDomain: newDomain.toUpperCase() // API expects uppercase (LEGAL or MEDICAL)
      });
      console.log('Domain updated successfully');
      setSuccessMessage(`Domain updated to ${newDomain}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating team domain:', err);
      setError('Failed to update team domain: ' + (err.response?.data?.message || err.message));
    }
  };
  
  const handleRegionChange = async (e) => {
    const newRegion = e.target.value;
    setSelectedRegion(newRegion);
    
    // Clear any previous messages
    setError(null);
    setSuccessMessage(null);
    
    if (!userProfile || !userProfile.teamId) {
      console.error('No team ID available to update locale');
      setError('Team information is missing. Cannot update region.');
      return;
    }
    
    try {
      console.log(`Updating team locale to ${newRegion}`);
      // Update team with new locale (teamLocale in the API)
      await updateTeam(userProfile.teamId, {
        teamLocale: newRegion // Using the country code directly (AUS, NZL, US, GBR)
      });
      console.log('Region updated successfully');
      setSuccessMessage(`Region updated to ${newRegion}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating team locale:', err);
      setError('Failed to update team region: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCaseClick = (caseId) => {
    navigate(`/matters/${caseId}`);
  };
  
  const handleDeleteCase = async (e, caseId) => {
    e.stopPropagation(); // Prevent triggering the card click
    
    if (window.confirm('Are you sure you want to delete this matter? This action cannot be undone.')) {
      try {
        await deleteMatter(userProfile.teamId, caseId);
        // Remove the case from the state
        setmatters(matters.filter(caseItem => caseItem.id !== caseId));
      } catch (err) {
        console.error('Error deleting matter:', err);
        setError('Failed to delete matter: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const getStatusClass = (status) => {
    const statusClasses = {
      [STATUS.NEW]: 'status-new',
      [STATUS.IN_PROGRESS]: 'status-active',
      [STATUS.PENDING]: 'status-pending',
      [STATUS.COMPLETED]: 'status-completed',
      [STATUS.CLOSED]: 'status-closed',
      [STATUS.CANCELLED]: 'status-cancelled'
    };
    
    return statusClasses[status] || 'status-default';
  };

  return (
    <div className="dashboard-container">
      {!headlessMode && (
        <>
          <Header 
            showTeamSelector={true}
            showDomainSelector={true}
            showRegionSelector={true}
            selectedDomain={selectedDomain}
            selectedRegion={selectedRegion}
            domains={domains}
            regions={regions}
            onDomainChange={handleDomainChange}
            onRegionChange={handleRegionChange}
            onTeamChange={handleTeamChange}
            onLogout={handleLogout}
          />
          <TabNavigation activeTab={activeTab} />
        </>
      )}

      {/* In headless mode, we always show the content. In normal mode, we check the active tab */}
      {(headlessMode || activeTab === TABS.MATTERS) && (
        <div className="dashboard-content">
          <div className="content-header">
            <h2>Matters</h2>
            <button 
              className="create-button"
              onClick={() => navigate('/matters/new')}
            >
              Create New Matter
            </button>
          </div>

          <div className="matter-filters">
            <button 
              className={caseFilter === FILTERS.ALL ? 'active' : ''} 
              onClick={() => setCaseFilter(FILTERS.ALL)}
            >
              All Matters
            </button>
            <button 
              className={caseFilter === FILTERS.NEW ? 'active' : ''} 
              onClick={() => setCaseFilter(FILTERS.NEW)}
            >
              New
            </button>
            <button 
              className={caseFilter === FILTERS.ACTIVE ? 'active' : ''} 
              onClick={() => setCaseFilter(FILTERS.ACTIVE)}
            >
              Active
            </button>
            <button 
              className={caseFilter === FILTERS.PENDING ? 'active' : ''} 
              onClick={() => setCaseFilter(FILTERS.PENDING)}
            >
              Pending
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          {loading ? (
            <div className="loading">Loading matters...</div>
          ) : (
            <div className="matter-list">
              {matters.length === 0 ? (
                <div className="no-matters">No matters found</div>
              ) : (
                matters
                  .filter(caseItem => !caseItem.isDeleted)
                  .map(caseItem => (
                    <div 
                      key={caseItem.id} 
                      className="matter-card"
                      onClick={() => handleCaseClick(caseItem.id)}
                    >
                      <div className="matter-header">
                        <h3>{caseItem.title}</h3>
                        <div className="matter-actions">
                          <span className={`matter-status ${getStatusClass(caseItem.status)}`}>
                            {caseItem.status}
                          </span>
                          <button 
                            className="delete-button"
                            onClick={(e) => handleDeleteCase(e, caseItem.id)}
                            title="Delete matter"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <p className="matter-description">{caseItem.description}</p>
                      <div className="matter-metadata">
                        <span>Files: {caseItem.fileCount || 0}</span>
                        <span>Conversations: {caseItem.chatCount || 0}</span>
                        <span>Updated: {new Date(caseItem.updatedAt || caseItem.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Dashboard;